// SharePoint export contract (issue #7 / ADR-0007).
// Write-only Document Export to a configured Microsoft 365 SharePoint
// document library. The vault row in our DB is the source of truth;
// SharePoint is opaque blob storage.

import { initContract } from '@ts-rest/core';
import { z } from 'zod';

const c = initContract();

// Fixed taxonomy slug list aligned to the nine PDF templates.
// Adding a new taxonomy requires updating both this list and the path
// layout in the export helper.
export const documentTaxonomySchema = z.enum([
    'suitability_letter',
    'board_pack',
    'breach_notification',
    'call_notes',
    'fee_invoice',
    'governance_paper',
    'monthly_report',
    'onboarding_pack',
    'reg_evidence_pack',
]);

export type DocumentTaxonomy = z.infer<typeof documentTaxonomySchema>;

export const vaultRowSchema = z.object({
    id: z.string(),
    producerType: z.string(),
    producerId: z.string(),
    versionStamp: z.string(),
    taxonomy: documentTaxonomySchema,
    title: z.string().nullable(),
    clientId: z.string().nullable(),
    clientName: z.string().nullable(),
    filename: z.string(),
    siteId: z.string(),
    driveId: z.string(),
    itemId: z.string(),
    webUrl: z.string().nullable(),
    byteSize: z.number().int().nonnegative().nullable(),
    exportedAt: z.string(),
    exportedBy: z.string().nullable(),
    lastExportedAt: z.string(),
    lastExportedBy: z.string().nullable(),
});

export type VaultRow = z.infer<typeof vaultRowSchema>;

export const sharePointStatusSchema = z.object({
    // True iff every env var needed for app-only Graph upload is set AND the
    // server's most recent probe of the configured drive succeeded. False
    // when any prerequisite is missing (no tenant id, no client secret, no
    // drive id), or when the probe failed (site grant not in place, drive
    // does not exist, network error). The Export button uses this directly
    // — true → render, false → hide. No demo-mode fallback (see ADR-0007).
    configured: z.boolean(),
    // Human-readable disclosure for the Integrations card. Distinguishes
    // "no config" from "config present but probe failed" without exposing
    // raw env or Graph error bodies.
    statusDetail: z.string(),
});

export type SharePointStatus = z.infer<typeof sharePointStatusSchema>;

export const exportSuitabilityLetterRequestSchema = z.object({
    producerId: z.string().min(1),
    versionStamp: z.string().min(1),
    taxonomy: documentTaxonomySchema,
    title: z.string().min(1),
    clientId: z.string().nullable(),
    clientName: z.string().nullable(),
    // base64-encoded PDF bytes. Browser renders the PDF (react-pdf), encodes
    // to base64, posts here; server decodes, uploads to Graph, writes vault
    // row atomically (row only persisted on successful upload — ADR-0007).
    pdfBase64: z.string().min(1),
});

export type ExportPdfRequest = z.infer<typeof exportSuitabilityLetterRequestSchema>;

export const exportPdfResponseSchema = z.object({
    vaultRow: vaultRowSchema,
});

export type ExportPdfResponse = z.infer<typeof exportPdfResponseSchema>;

export const listVaultResponseSchema = z.object({
    rows: z.array(vaultRowSchema),
});

export type ListVaultResponse = z.infer<typeof listVaultResponseSchema>;

export const sharePointContract = c.router({
    status: {
        method: 'GET',
        path: '/api/sharepoint/status',
        responses: {
            200: sharePointStatusSchema,
        },
        summary: 'Report whether SharePoint export is configured and reachable.',
    },
    list: {
        method: 'GET',
        path: '/api/sharepoint/vault',
        responses: {
            200: listVaultResponseSchema,
        },
        summary: 'List the firm document-vault register (newest export first).',
    },
    exportPdf: {
        method: 'POST',
        path: '/api/sharepoint/export',
        body: exportSuitabilityLetterRequestSchema,
        responses: {
            200: exportPdfResponseSchema,
            503: z.object({ error: z.string() }),
        },
        summary: 'Upload a rendered PDF to SharePoint and write a vault row.',
    },
});
